import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ArrowLeft, Calendar, CreditCard, Wallet, BarChart3, ChevronDown, ChevronRight, FileText, Plus, Eye } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const STATUS_LABELS = { nou: "Nou", in_verificare: "In verificare", acceptat: "Acceptat", in_lucru: "In lucru", finalizat: "Finalizat", respins: "Respins" };
const STATUS_COLORS = {
  nou: "bg-blue-500/20 text-blue-400", in_verificare: "bg-yellow-500/20 text-yellow-400",
  acceptat: "bg-emerald-500/20 text-emerald-400", in_lucru: "bg-purple-500/20 text-purple-400",
  finalizat: "bg-green-500/20 text-green-400", respins: "bg-red-500/20 text-red-400"
};

const getMonthKey = (dateStr) => {
  if (!dateStr) return "necunoscut";
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthLabel = (key) => {
  if (key === "necunoscut") return "Necunoscut";
  const [year, month] = key.split("-");
  return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
};

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const MonthSection = ({ monthKey, children, count, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-700 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-700/50 hover:bg-gray-700 transition-colors text-left">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-white text-sm">{getMonthLabel(monthKey)}</span>
          <span className="text-xs text-gray-400 bg-gray-600 px-2 py-0.5 rounded-full">{count}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div>{children}</div>}
    </div>
  );
};

const AfiliatDetailPanel = ({ afiliat, adminToken, onBack, onViewDosar }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState("dosare");
  const [showPayForm, setShowPayForm] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");

  const fetchDetail = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/afiliati/${afiliat.id}/detail`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (!res.ok) throw new Error("Eroare");
      const d = await res.json();
      setData(d);
    } catch {
      toast.error("Eroare la incarcarea detaliilor");
    } finally {
      setLoading(false);
    }
  }, [afiliat.id, adminToken]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const submitPayment = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) { toast.error("Introdu o suma valida"); return; }
    try {
      await fetch(`${API}/admin/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ afiliat_id: afiliat.id, suma: parseFloat(payAmount), nota: payNote || null })
      });
      toast.success("Plata inregistrata!");
      setShowPayForm(false);
      setPayAmount("");
      setPayNote("");
      fetchDetail();
    } catch {
      toast.error("Eroare la inregistrarea platii");
    }
  };

  const currentMonth = getCurrentMonthKey();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const dosare = data.dosare || [];
  const plati = data.plati || [];

  // Group dosare by month
  const currentMonthDosare = dosare.filter(d => getMonthKey(d.created_at) === currentMonth);
  const historyDosare = dosare.filter(d => getMonthKey(d.created_at) !== currentMonth);
  const historyDosareMonths = {};
  historyDosare.forEach(d => {
    const k = getMonthKey(d.created_at);
    if (!historyDosareMonths[k]) historyDosareMonths[k] = [];
    historyDosareMonths[k].push(d);
  });
  const sortedHistDosMonths = Object.keys(historyDosareMonths).sort().reverse();

  // Group plati by month
  const currentMonthPlati = plati.filter(p => getMonthKey(p.data) === currentMonth);
  const historyPlati = plati.filter(p => getMonthKey(p.data) !== currentMonth);
  const historyPlatiMonths = {};
  historyPlati.forEach(p => {
    const k = getMonthKey(p.data);
    if (!historyPlatiMonths[k]) historyPlatiMonths[k] = [];
    historyPlatiMonths[k].push(p);
  });
  const sortedHistPayMonths = Object.keys(historyPlatiMonths).sort().reverse();

  const renderDosarRow = (d) => (
    <div key={d.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-700/30 border-b border-gray-700/50 last:border-0" data-testid={`afiliat-dosar-${d.id}`}>
      <div className="flex items-center gap-4">
        <span className="font-mono text-sm font-semibold text-emerald-400">{d.numar_dosar}</span>
        <span className="text-sm text-white">{d.nume_prenume}</span>
        <span className="text-xs text-gray-500">{d.created_at?.slice(0, 10)}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[d.status] || ""}`}>{STATUS_LABELS[d.status] || d.status}</span>
        {d.discount > 0 && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">-{d.discount}%</span>}
        {onViewDosar && (
          <button onClick={() => onViewDosar(d)} className="p-1 text-emerald-400 hover:bg-emerald-500/20 rounded">
            <Eye className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  const renderPlataRow = (p) => (
    <div key={p.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-700/30 border-b border-gray-700/50 last:border-0" data-testid={`afiliat-plata-${p.id}`}>
      <div className="flex items-center gap-4">
        <CreditCard className="w-4 h-4 text-blue-400" />
        <span className="text-sm text-white">{p.data?.slice(0, 10)}</span>
        {p.nota && <span className="text-xs text-gray-400">{p.nota}</span>}
      </div>
      <span className="text-sm font-bold text-emerald-400">+{p.suma} lei</span>
    </div>
  );

  const emptyState = (icon, text) => (
    <div className="py-8 text-center">
      {icon}
      <p className="text-gray-500 text-sm mt-2">{text}</p>
    </div>
  );

  return (
    <div data-testid="afiliat-detail-panel">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white" data-testid="afiliat-detail-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">{afiliat.prenume} {afiliat.nume}</h2>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <span>{afiliat.email}</span>
            <span className="font-mono text-emerald-400">{afiliat.cod_afiliat}</span>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-xs">Total dosare</p>
          <p className="text-xl font-bold text-white">{data.total_dosare}</p>
        </div>
        <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-xs">Comision total</p>
          <p className="text-xl font-bold text-emerald-400">{data.comision_total} lei</p>
        </div>
        <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-xs">Platit</p>
          <p className="text-xl font-bold text-blue-400">{data.comision_platit} lei</p>
        </div>
        <div className="bg-gray-700/50 rounded-xl p-4 border border-gray-700">
          <p className="text-gray-400 text-xs">De plata</p>
          <p className="text-xl font-bold text-amber-400">{data.comision_de_plata} lei</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => setSubTab("dosare")} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === "dosare" ? "bg-emerald-500 text-white" : "bg-gray-700/50 text-gray-400 hover:text-white"}`} data-testid="afiliat-subtab-dosare">
          Dosare ({dosare.length})
        </button>
        <button onClick={() => setSubTab("plati")} className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === "plati" ? "bg-emerald-500 text-white" : "bg-gray-700/50 text-gray-400 hover:text-white"}`} data-testid="afiliat-subtab-plati">
          Plati ({plati.length})
        </button>
        {subTab === "plati" && (
          <button onClick={() => setShowPayForm(true)} className="ml-auto px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 flex items-center gap-1" data-testid="add-payment-btn">
            <Plus className="w-4 h-4" /> Plata noua
          </button>
        )}
      </div>

      {/* ===== DOSARE ===== */}
      {subTab === "dosare" && (
        <div className="space-y-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid="afiliat-dosare-luna-curenta">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-400" />
              <span className="font-semibold text-white text-sm">Luna curenta - {getMonthLabel(currentMonth)}</span>
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">{currentMonthDosare.length}</span>
            </div>
            {currentMonthDosare.length === 0
              ? emptyState(<FileText className="w-8 h-8 text-gray-600 mx-auto" />, "Niciun dosar in luna curenta")
              : currentMonthDosare.map(renderDosarRow)}
          </div>

          {sortedHistDosMonths.length > 0 && (
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Istoric dosare</p>
              <div className="space-y-2">
                {sortedHistDosMonths.map(mk => (
                  <MonthSection key={mk} monthKey={mk} count={historyDosareMonths[mk].length} defaultOpen={false}>
                    {historyDosareMonths[mk].map(renderDosarRow)}
                  </MonthSection>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ===== PLATI ===== */}
      {subTab === "plati" && (
        <div className="space-y-4">
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden" data-testid="afiliat-plati-luna-curenta">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-400" />
              <span className="font-semibold text-white text-sm">Plati luna curenta - {getMonthLabel(currentMonth)}</span>
              <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">{currentMonthPlati.length}</span>
            </div>
            {currentMonthPlati.length === 0
              ? emptyState(<CreditCard className="w-8 h-8 text-gray-600 mx-auto" />, "Nicio plata in luna curenta")
              : currentMonthPlati.map(renderPlataRow)}
          </div>

          {sortedHistPayMonths.length > 0 && (
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Istoric plati</p>
              <div className="space-y-2">
                {sortedHistPayMonths.map(mk => (
                  <MonthSection key={mk} monthKey={mk} count={historyPlatiMonths[mk].length} defaultOpen={false}>
                    {historyPlatiMonths[mk].map(renderPlataRow)}
                  </MonthSection>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Form Modal */}
      {showPayForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-700">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center"><Wallet className="w-5 h-5 text-emerald-400" /></div>
              <div><h3 className="text-lg font-bold text-white">Inregistreaza Plata</h3><p className="text-sm text-gray-400">{afiliat.prenume} {afiliat.nume}</p></div>
            </div>
            <div className="bg-gray-700/50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-gray-400">Comision total</p><p className="text-white font-medium">{data.comision_total} lei</p></div>
                <div><p className="text-gray-400">Deja platit</p><p className="text-blue-400 font-medium">{data.comision_platit} lei</p></div>
                <div className="col-span-2"><p className="text-gray-400">De plata</p><p className="text-2xl text-amber-400 font-bold">{data.comision_de_plata} lei</p></div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Suma platita (lei)</label>
                <input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder={`Max: ${data.comision_de_plata}`} className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" data-testid="detail-payment-amount" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nota (optional)</label>
                <input type="text" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Ex: Transfer bancar apr 2026" className="w-full h-12 px-4 rounded-xl border border-gray-600 bg-gray-700 text-white" data-testid="detail-payment-note" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowPayForm(false); setPayAmount(""); setPayNote(""); }} className="flex-1 h-12 border border-gray-600 text-gray-300 rounded-full hover:bg-gray-700">Anuleaza</button>
              <button onClick={submitPayment} className="flex-1 h-12 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 flex items-center justify-center gap-2" data-testid="detail-confirm-payment"><Plus className="w-4 h-4" />Confirma</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AfiliatDetailPanel;
